import React from 'react'
import { ScrollView } from 'react-native'

import { Slider } from '@miblanchard/react-native-slider'
import { publishEvent } from '@zet/analytics'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import { B4, B5, H6, SH3 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'

const EmiCalculator = () => {
  const [amount, setAmount] = React.useState<number>(100000)
  const [month, setMonth] = React.useState<number>(18)
  const [roi, setRoi] = React.useState<number>(15)

  const getCalculatedEmiAmount = () => {
    const rate = roi / 12 / 100
    const roiPowInTenure = Math.pow(1 + rate, month)
    const totalEMI = (amount * rate * roiPowInTenure) / (roiPowInTenure - 1)
    const emi = isNaN(totalEMI) ? 0 : totalEMI.toFixed(0)
    return emi
  }

  const MaxMin = ({
    maxValue,
    minValue,
  }: {
    maxValue: string
    minValue: string
  }) => {
    return (
      <XStack className="justify-between pb-3">
        <YStack>
          <Stack>
            <B5 className="text-grays-60">{i18n.t('calculater.min')}</B5>
          </Stack>
          <Stack>
            <B4 className="text-grays-80">{minValue}</B4>
          </Stack>
        </YStack>
        <YStack>
          <Stack>
            <B5 className="text-grays-60">{i18n.t('calculater.max')}</B5>
          </Stack>
          <Stack>
            <B4 className="text-grays-80">{maxValue}</B4>
          </Stack>
        </YStack>
      </XStack>
    )
  }

  const ThumbPressBar = () => (
    <Stack className="border-success-150 bg-grays-20 h-4  w-4 items-center justify-center rounded-md border	">
      <Stack className="bg-success-150 h-2 w-2 rounded-sm"></Stack>
    </Stack>
  )

  const SliderHeading = ({
    data,
    isAmount,
    isMonth,
    heading,
    isRoi,
    emiAmount,
  }: {
    data: number | number[]
    isAmount?: boolean
    isMonth?: boolean
    heading: string
    isRoi?: boolean
    emiAmount?: number
  }) => {
    return (
      <Stack>
        <SH3 className="text-grays-60 mb-1 pb-1 pt-3">{heading}</SH3>
        {isAmount && (
          <H6 family="graphie" className="text-success-150 mb-5">
            ₹{Array.isArray(data) ? data.join(' - ') : data}
          </H6>
        )}
        {isMonth && (
          <H6 family="graphie" className="text-grays-80 mb-5">
            {`₹${emiAmount}/Month for ${
              Array.isArray(data) ? data.join(' - ') : data
            } Months`}
          </H6>
        )}
        {isRoi && (
          <H6 family="graphie" className="text-grays-80 mb-5">
            ₹{Array.isArray(data) ? data.join(' - ') : data}% per Annum
          </H6>
        )}
      </Stack>
    )
  }
  return (
    <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
      <YStack className="rounded-2xl bg-white px-3 pt-3">
        <SliderHeading
          data={amount}
          isAmount={true}
          heading={i18n.t('calculater.loanAmount.heading')}
        />
        <Slider
          minimumValue={1000}
          maximumValue={500000}
          step={1}
          value={amount}
          minimumTrackTintColor="#8DEA8B"
          renderThumbComponent={() => <ThumbPressBar />}
          onValueChange={(data) => setAmount(data)}
          onSlidingComplete={() => {
            publishEvent('click_action', {
              item_name: 'emi_calculator_slider_change',
            })
          }}
          trackStyle={{ height: 10, backgroundColor: '#F5F7FB' }}
        />
        <MaxMin
          maxValue={i18n.t('calculater.loanAmount.maxAmount')}
          minValue={i18n.t('calculater.loanAmount.minAmount')}
        />
      </YStack>
      <YStack className="mt-3 bg-white px-3">
        <SliderHeading
          data={month}
          isMonth={true}
          heading={i18n.t('calculater.emiTenure.heading')}
          emiAmount={getCalculatedEmiAmount()}
        />
        <Slider
          minimumValue={3}
          maximumValue={36}
          step={1}
          value={month}
          renderThumbComponent={() => <ThumbPressBar />}
          onValueChange={(data) => setMonth(data)}
          onSlidingComplete={() => {
            publishEvent('click_action', {
              item_name: 'emi_calculator_slider_change',
            })
          }}
          trackMarks={[9, 18, 27, 36]}
          renderTrackMarkComponent={() => (
            <Stack
              className="bg-slate-600"
              style={{ width: 1, height: 10 }}
            ></Stack>
          )}
          trackStyle={{ height: 10, backgroundColor: '#F5F7FB' }}
          minimumTrackTintColor="#2F3440"
        />
        <MaxMin
          maxValue={i18n.t('calculater.emiTenure.maxMonth')}
          minValue={i18n.t('calculater.emiTenure.minMonth')}
        />
      </YStack>
      <YStack className="mt-3 bg-white px-3">
        <SliderHeading
          data={roi}
          isRoi={true}
          heading={i18n.t('calculater.roi.heading')}
        />
        <Slider
          minimumValue={10}
          maximumValue={20}
          step={0.25}
          value={roi}
          renderThumbComponent={() => <ThumbPressBar />}
          onValueChange={(data) => setRoi(data)}
          onSlidingComplete={() => {
            publishEvent('click_action', {
              item_name: 'emi_calculator_slider_change',
            })
          }}
          trackMarks={[5, 10, 15, 20]}
          renderTrackMarkComponent={() => (
            <Stack
              className="bg-slate-600"
              style={{ width: 1, height: 10 }}
            ></Stack>
          )}
          trackStyle={{ height: 10, backgroundColor: '#F5F7FB' }}
          minimumTrackTintColor="#2F3440"
        />
        <MaxMin
          maxValue={i18n.t('calculater.roi.maxRoi')}
          minValue={i18n.t('calculater.roi.minRoi')}
        />
      </YStack>
      {/* <Stack className="mt-3 bg-white p-3">
        <YStack className="bg-grays-20 p-2">
          <B2 className="text-grays-60">
            {i18n.t('calculater.processingFee')}
          </B2>
          <SH3>500</SH3>
        </YStack>
      </Stack> */}
    </ScrollView>
  )
}

export default EmiCalculator
